import React from "react";

/**
 * v0.4.35 P6C — Filing cadence badge.
 * v0.4.37 U1 — `pendingVies` prop: when > 0, appends "· N pending VIES"
 * and forces a yellow (amber) tint on the badge background.
 *
 * Headline = the most urgent (earliest) real deadline, colour-
 * bucketed (greater than 14d green; 7 to 14d amber; less than 7d red). Hover reveals
 * every upcoming deadline including the OSS_NOT_CONFIGURED
 * warning when present (operator D-P6C-4=c).
 *
 * v0.6.12 — a deadline whose period already has a FROZEN calc is marked
 * `filed` by the backend, and the headline SKIPS it. Nothing is hidden: the
 * filed row still appears in the tooltip, still shows its real date, and is
 * annotated with when it was finalised. Two deliberate choices:
 *
 *   - "finalised", not "filed" or "submitted". All the data model records is
 *     that a calc for that period was frozen in this app. Only UK MTD
 *     actually posts to an authority; Italy's XML is generated, not sent.
 *     Saying more than that on a compliance screen would be a lie of tone.
 *   - if EVERY deadline is filed, the headline falls back to the first one
 *     rather than the badge going blank. A blank badge reads as "no
 *     deadlines", which is a different and wrong statement.
 */
const COLOUR = {
  green: "border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300",
  amber: "border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300",
  red: "border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/40 text-red-700 dark:text-red-300",
};
// U1C=a — yellow tint when pendingVies > 0 (does NOT override red,
// since a red deadline outranks a pending-VIES warning).
const YELLOW_TINT = "border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300";

export const CadenceBadge = ({ deadlines, pendingVies = 0 }) => {
  if (!deadlines || deadlines.length === 0) {
    const cls = pendingVies > 0
      ? `tabular-nums text-[10px] px-2 py-1 border ${YELLOW_TINT} rounded`
      : "tabular-nums text-[10px] text-slate-500 dark:text-slate-400 px-2 py-1 border border-slate-300 dark:border-slate-700";
    const text = pendingVies > 0
      ? `no deadlines · ${pendingVies} pending VIES`
      : "no deadlines";
    return (
      <span
        data-testid="cadence-badge"
        data-testid-pending={pendingVies > 0 ? "yes" : "no"}
        className={cls}
      >{text}</span>
    );
  }
  const real = deadlines.filter((d) => d.due_date);
  // Lead with the first deadline still OUTSTANDING. A filed period keeps its
  // real date and colour in the tooltip; it just stops crying wolf.
  const outstanding = real.filter((d) => !d.filed);
  const headline = outstanding[0] || real[0] || deadlines[0];
  const tooltip = deadlines
    .map((d) => {
      if (!d.due_date) return `${d.label}`;
      const base = `${d.label} → ${d.due_date} (${d.days_until}d)`;
      if (!d.filed) return base;
      const when = (d.filed_at || "").slice(0, 10);
      const ref = d.filing_reference ? ` · ${d.filing_reference}` : "";
      return `${base} · finalised${when ? " " + when : ""}${ref}`;
    })
    .join("\n");
  // U1C=a — pendingVies > 0 forces yellow tint UNLESS the deadline
  // is already red (red severity outranks).
  // A filed headline (only possible when everything is filed) must not shout.
  const severity = headline.filed ? "green" : (headline.severity || "amber");
  const colour = (pendingVies > 0 && severity !== "red")
    ? YELLOW_TINT
    : COLOUR[severity];
  const headlineText = headline.due_date
    ? (headline.filed
        ? `${headline.label} · finalised`
        : `${headline.label} · ${headline.days_until}d`)
    : headline.label;
  // U1B=a — only render suffix when pendingVies > 0.
  const suffix = pendingVies > 0
    ? ` · ${pendingVies} pending VIES`
    : "";
  return (
    <span
      data-testid="cadence-badge"
      data-testid-pending={pendingVies > 0 ? "yes" : "no"}
      title={tooltip}
      className={`tabular-nums text-[10px] px-2 py-1 border ${colour} rounded cursor-help`}
    >
      <span data-testid="cadence-badge-deadline">{headlineText}</span>
      {pendingVies > 0 && (
        <span data-testid="cadence-badge-pending">{suffix}</span>
      )}
    </span>
  );
};
